/**
 * PromoBanner — modal de banner promocional pós-login
 * Usado no ChatHubPage para exibir promoções ativas
 * Suporta modo countdown e banner normal
 */
import { T } from '../../styles/tokens';
import { useEffect, useState } from 'preact/hooks';
import { Signal } from '@preact/signals';
import { SlotsRemainingBadge } from './SlotsRemainingBadge';
import { CountdownTimer } from './CountdownTimer';

interface PromoBannerProps {
  /** Signal indicando se usuário está logado */
  isLoggedIn: Signal<boolean>;
  /** Função para buscar banners (GET /promotions/banners) */
  fetchBanners: () => Promise<any[]>;
  /** Função para dispensar banner (POST /promotions/banners/:id/dismiss) */
  dismissBanner: (viewId: string) => Promise<void>;
}

export function PromoBanner({ isLoggedIn, fetchBanners, dismissBanner }: PromoBannerProps) {
  const [banners, setBanners] = useState<any[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!isLoggedIn.value) return;
    fetchBanners().then(b => { if (b.length > 0) setBanners(b); }).catch(() => {});
  }, []);

  // Atualizar now a cada segundo para transição countdown → banner
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  if (banners.length === 0) return null;
  const banner = banners[currentIdx];
  if (!banner) return null;

  const dismiss = () => {
    dismissBanner(banner.banner_view_id).catch(() => {});
    const next = banners.filter((_: any, i: number) => i !== currentIdx);
    setBanners(next);
    setCurrentIdx(0);
  };

  // Verificar se deve mostrar countdown (starts_at no futuro e countdown habilitado)
  const startsAt = banner.starts_at ? new Date(banner.starts_at).getTime() : 0;
  const isCountdown = startsAt > now && banner.countdown_enabled;

  // Cores e conteúdo baseado no modo
  const bgColor = isCountdown ? (banner.countdown_bg_color || T.purple) : (banner.banner_bg_color || T.primary);
  const txtColor = isCountdown ? (banner.countdown_text_color || T.text) : T.text;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 99999,
      background: T.bgOverlay, display: 'flex', alignItems: 'center', justifyContent: 'center',
      animation: 'fadeIn 0.2s ease-out',
    }} onClick={dismiss}>
      <div onClick={(e: any) => e.stopPropagation()} style={{
        background: bgColor, borderRadius: 16, padding: 32,
        maxWidth: 480, width: '90%', color: txtColor, position: 'relative',
        boxShadow: `0 20px 60px ${T.blackAlpha40}`,
        transition: 'background 0.5s ease',
      }}>
        <button onClick={dismiss} style={{
          position: 'absolute', top: 12, right: 12, background: T.borderHover,
          border: 'none', borderRadius: '50%', width: 28, height: 28, cursor: 'pointer',
          color: txtColor, fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>✕</button>

        {isCountdown ? (
          /* Modo Countdown */
          <div style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginBottom: 8 }}>
              {banner.countdown_image_url && (() => {
                const url = banner.countdown_image_url.toLowerCase();
                const isVideo = url.match(/\.(mp4|webm|mov)(\?|$)/);
                return isVideo
                  ? <video src={banner.countdown_image_url} autoPlay muted loop playsInline style={{ width: 60, height: 60, borderRadius: 10, objectFit: 'cover' }} />
                  : <img src={banner.countdown_image_url} alt="" style={{ width: 60, height: 60, borderRadius: 10, objectFit: 'cover' }} />;
              })()}
              <div style={{ fontSize: 22, fontWeight: 700 }}>{banner.countdown_title || 'Promoção chegando!'}</div>
            </div>
            <CountdownTimer startsAt={banner.starts_at} textColor={txtColor} />
            {banner.countdown_message && (
              <div style={{ fontSize: 14, opacity: 0.9, marginTop: 8 }}>{banner.countdown_message}</div>
            )}
            <SlotsRemainingBadge max={banner.max_redemptions || 0} current={banner.current_redemptions || 0} textColor={txtColor} />
          </div>
        ) : (
          /* Modo Banner Normal */
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              {banner.banner_image_url && (() => {
                const url = banner.banner_image_url.toLowerCase();
                const isVideo = url.match(/\.(mp4|webm|mov)(\?|$)/) || url.includes('youtube.com') || url.includes('youtu.be');
                return isVideo
                  ? <video src={banner.banner_image_url} autoPlay muted loop playsInline style={{ width: 140, height: 90, borderRadius: 12, objectFit: 'cover' }} />
                  : <img src={banner.banner_image_url} alt="" style={{ width: 80, height: 80, borderRadius: 12, objectFit: 'cover' }} />;
              })()}
              <div>
                <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>{banner.banner_title}</div>
                <div style={{ fontSize: 15, opacity: 0.9, lineHeight: 1.5 }}>{banner.banner_message}</div>
                <SlotsRemainingBadge max={banner.max_redemptions || 0} current={banner.current_redemptions || 0} />
              </div>
            </div>
            <div style={{ textAlign: 'right', marginTop: 16, fontSize: 12, opacity: 0.6 }}>
              {(banner.view_count || 0) + 1} de {banner.banner_show_count}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
